import React, { createContext, useEffect, useMemo, useRef } from 'react'
import { useAppDispatch } from '@/redux/hooks'
import { useOffline } from '@/hooks/useOffline'
import { useConnectivity } from '@/hooks/useConnectivity'
import { useEcho } from '@/hooks/useEcho'
import { clearPosOfflineCatalog, startBackgroundSync } from '@/hooks/usePosOffline'
import { OperationDateProvider } from './OperationDateProvider'

/**
 * SystemInfrastructureProvider
 *
 * Elevates global, route-independent infrastructure to the application root so
 * it initializes exactly once per session:
 *   - Connectivity model (`navigator.onLine` + periodic GET /api/health probe
 *     via useConnectivity, plus marks from the RTK base query)
 *   - Offline queue drain (startBackgroundSync — FIFO replay of queued sales,
 *     with backoff + cross-tab lock; the POS page hooks also drain on their
 *     own 'online' events, both paths share one module-level in-flight guard)
 *   - WebSocket connection (Laravel Echo / Reverb) for real-time stock updates
 *   - Hardware (Serial Port + USB) device watchers
 *   - WCAG keyboard navigation listener (focus outline toggling)
 *
 * Replaces the legacy Vue `SystemInfrastructureProvider.vue` which mounted these
 * from `App.vue` to prevent memory leaks caused by per-route re-initialization.
 */

interface SystemInfrastructureContextValue {
  /** Live connectivity state from the app-wide probe (read-only snapshot). */
  connectivity: ReturnType<typeof useConnectivity>
}

const SystemInfrastructureContext = createContext<SystemInfrastructureContextValue>({
  connectivity: {
    navigatorOnline: true,
    serverReachable: 'unknown',
    degraded: false,
    lastCheckedAt: null,
    isEffectivelyOnline: true,
    probeNow: () => Promise.resolve(true),
  },
})

export const SystemInfrastructureProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  useAppDispatch();
  const bootedRef = useRef(false)
  const catalogReloadRef = useRef(false)

  // 1. Offline detection — hooks subscribe per-instance to navigator.onLine;
  //    useOffline mirrors those events into the connectivity store.
  useOffline()

  // 2. Connectivity probe — the "periodic ping" this provider always claimed:
  //    navigator.onLine + /api/health, reference-counted so re-mounts never
  //    stack intervals. `useSystemInfrastructure().connectivity` exposes it.
  const connectivity = useConnectivity()

  // 3. WebSocket — global Echo connection. `useEcho` no-ops if already connected.
  useEcho()

  useEffect(() => {
    // Genuinely mount-once: the ref guard makes this body run a single time,
    // so a dependency (e.g. dispatch) could only re-trigger a teardown that
    // the guard then skips - leaving every listener unregistered.
    if (bootedRef.current) return
    bootedRef.current = true

    // 4. WCAG keyboard navigation: show focus outline only for keyboard users,
    //    hide it for mouse users (avoids the dotted ring on button clicks).
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Tab' || e.key === 'ArrowLeft' || e.key === 'ArrowRight' || e.key === 'ArrowUp' || e.key === 'ArrowDown') {
        document.body.classList.add('user-is-tabbing')
      }
    }
    const handleMouseDown = () => {
      document.body.classList.remove('user-is-tabbing')
    }
    window.addEventListener('keydown', handleKeyDown)
    window.addEventListener('mousedown', handleMouseDown)

    // 5. App-wide offline queue drain: once at boot, on every 'online'
    //    transition and whenever the tab becomes visible again. The drain
    //    itself re-checks connectivity + is guarded against overlap, so extra
    //    triggers are cheap no-ops.
    const drainQueue = () => startBackgroundSync()
    drainQueue()
    window.addEventListener('online', drainQueue)
    const handleVisibility = () => {
      if (document.visibilityState === 'visible') drainQueue()
    }
    document.addEventListener('visibilitychange', handleVisibility)

    // 6. Service Worker cache cleanup message listener (matches legacy behavior)
    const handleSWMessage = (event: MessageEvent) => {
      if (event.data?.type === 'CACHE_CLEARED') {
        // The worker wiped its caches — the offline catalog may reference
        // assets that no longer exist. Purge the catalog/settings stores, then
        // reload so the app re-preloads against fresh caches.
        //
        // NOT clearPosOfflineData(): that is a full deleteDatabase and would
        // take `pending_sales` with it. A version bump while the drain has not
        // yet run would silently destroy sales whose cash is already in the
        // drawer. clearPosOfflineCatalog() keeps the unsynced queue rows —
        // they survive the reload and replay on the next drain.
        if (catalogReloadRef.current) return
        catalogReloadRef.current = true
        void (async () => {
          try {
            await clearPosOfflineCatalog()
            window.location.reload()
          } catch (error) {
            // Purge failed (IndexedDB blocked/unavailable) — reloading would
            // just repeat the stale-cache cycle, so stay up and retry next time.
            catalogReloadRef.current = false
            console.error('Offline catalog purge failed, skipping cache-cleared reload', error)
          }
        })()
      }
    }
    if ('serviceWorker' in navigator) {
      navigator.serviceWorker.addEventListener('message', handleSWMessage)
    }

    return () => {
      window.removeEventListener('keydown', handleKeyDown)
      window.removeEventListener('mousedown', handleMouseDown)
      window.removeEventListener('online', drainQueue)
      document.removeEventListener('visibilitychange', handleVisibility)
      if ('serviceWorker' in navigator) {
        navigator.serviceWorker.removeEventListener('message', handleSWMessage)
      }
    }
     
  }, [])

  const value = useMemo<SystemInfrastructureContextValue>(() => ({ connectivity }), [connectivity])
  return (
    <SystemInfrastructureContext.Provider value={value}>
      <OperationDateProvider>{children}</OperationDateProvider>
    </SystemInfrastructureContext.Provider>
  )
}

export default SystemInfrastructureProvider

export const useSystemInfrastructure = () => React.useContext(SystemInfrastructureContext)
